import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Pencil, Check, X, Download, MoreVertical, Archive } from 'lucide-react';
import BotaoVoltar from '../../../components/BotaoVoltar/BotaoVoltar';
import { FundoForm, DocumentoAnexo, DocumentoVersao } from '../fundo_liquido/types';
import { ResumoSecoes, ResumoDocumentos } from '../fundo_liquido/revisao';
import FundoEditDados from './FundoEditDados';
import ColecoesEdit from './ColecoesEdit';
import { Opt } from '../fundo_liquido/mock';
import { useToast } from '../../../components/toast_notification/ToastProvider';
import { atualizarFundo, detalharFundo, enviarDocumento, baixarDocumento, removerFundo, listarPrestadorOpts } from '../fundo_liquido/fundosService';
import { exportarFundoXlsx } from '../fundo_liquido/exportFundos';
import { maskCnpj } from '../fundo_liquido/fields';
import '../fundo_liquido/style.css'; // estilos das seções e dos campos (fl-rev-*, fl-field, fl-table...)
import './style.css';

interface Props {
  fundoId: number;
  onVoltar: () => void;
}

const SkBar: React.FC<{ w?: string | number; h?: number; className?: string }> = ({ w = '100%', h = 12, className }) => (
  <span
    className={`fd-sk${className ? ` ${className}` : ''}`}
    style={{ width: typeof w === 'number' ? `${w}px` : w, height: h }}
  />
);

const SkField: React.FC = () => (
  <div className="fl-rev-item">
    <SkBar w="50%" h={9} className="fd-sk--mb" />
    <SkBar w="80%" h={13} />
  </div>
);

const SkSection: React.FC<{ titleW?: string; n: number }> = ({ titleW = '120px', n }) => (
  <div className="fl-rev-section">
    <div className="fd-sk-sectitle"><SkBar w={titleW} h={10} /></div>
    <div className="fl-rev-grid">
      {Array.from({ length: n }).map((_, i) => <SkField key={i} />)}
    </div>
  </div>
);

const SkeletonDetalhe: React.FC<{ onVoltar: () => void }> = ({ onVoltar }) => (
  <div className="fd-screen">
    <div className="fd-head">
      <div className="fd-head-row">
        <div className="fd-head-main">
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <BotaoVoltar onClick={onVoltar} />
            <SkBar w="280px" h={24} />
          </div>
          <div style={{ marginTop: 8 }}><SkBar w="150px" h={13} /></div>
        </div>
        <div className="fd-actions"><SkBar w={96} h={34} /></div>
      </div>
    </div>
    <div className="fd-body fl-resumo" aria-busy="true" aria-label="Carregando fundo">
      <SkSection titleW="110px" n={3} />
      <SkSection titleW="130px" n={12} />
      <SkSection titleW="120px" n={5} />
      <div className="fl-rev-section">
        <div className="fd-sk-sectitle"><SkBar w="140px" h={10} /></div>
        <SkBar h={38} className="fd-sk--mb" />
        <SkBar h={30} className="fd-sk--mb" />
        <SkBar h={30} />
      </div>
    </div>
  </div>
);

/**
 * Tela de detalhe do fundo com edição inline. Busca o agregado via GET /fundos/{id}.
 * "Editar" troca a própria página para o modo de edição (campos viram inputs);
 * Salvar persiste via PUT /fundos/{id} e recarrega; Cancelar sai do modo.
 */
const FundoDetalhe: React.FC<Props> = ({ fundoId, onVoltar }) => {
  const toast = useToast();
  const [salvo, setSalvo] = useState<FundoForm | null>(null); // estado persistido (servidor)
  const [form, setForm] = useState<FundoForm | null>(null);   // estado em edição
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [editando, setEditando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [removendo, setRemovendo] = useState(false);
  const [menuAberto, setMenuAberto] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const [prestadorOpts, setPrestadorOpts] = useState<Opt[]>([]); // opções do select de prestador (edição)

  // prestadores reais da API (para o select ao editar/adicionar vínculos)
  useEffect(() => {
    listarPrestadorOpts().then(setPrestadorOpts).catch(() => { /* silencioso: select fica vazio */ });
  }, []);

  // fecha o menu de ações ao clicar fora
  useEffect(() => {
    if (!menuAberto) return;
    const fora = (e: MouseEvent) => {
      if (!menuRef.current?.contains(e.target as Node)) setMenuAberto(false);
    };
    document.addEventListener('mousedown', fora);
    return () => document.removeEventListener('mousedown', fora);
  }, [menuAberto]);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      const f = await detalharFundo(fundoId);
      setSalvo(f);
      setForm(f);
      setEditando(false);
    } catch (e: any) {
      setErro(e?.message ?? 'Falha ao carregar o fundo.');
      setSalvo(null);
      setForm(null);
    } finally {
      setCarregando(false);
    }
  }, [fundoId]);

  useEffect(() => { carregar(); }, [carregar]);

  const entrar = () => { setForm(salvo); setEditando(true); };
  const cancelar = () => { setForm(salvo); setEditando(false); };
  const salvar = async () => {
    if (salvando || !form) return;
    setSalvando(true);
    try {
      await atualizarFundo(fundoId, form);
      toast.success('Fundo atualizado com sucesso.');
      await carregar(); // recarrega o estado real (reconciliação/SCD2 no backend)
    } catch (e: any) {
      const det = e?.details ? ` — ${JSON.stringify(e.details)}` : '';
      toast.error(`Falha ao salvar: ${e?.message ?? 'erro'}${det}`);
    } finally {
      setSalvando(false);
    }
  };

  const patch = (p: Partial<FundoForm>) => setForm(prev => (prev ? { ...prev, ...p } : prev));

  const arquivar = async () => {
    if (removendo || !salvo) return;
    setMenuAberto(false);
    const nome = salvo.nome_fantasia || salvo.razao_social || `#${fundoId}`;
    const ok = window.confirm(
      `Arquivar o fundo "${nome}"?\n\nEle deixará de aparecer nas listagens (exclusão lógica, ` +
      'recuperável). Inclui classes, contas, vínculos e condições.',
    );
    if (!ok) return;
    setRemovendo(true);
    try {
      await removerFundo(fundoId);
      toast.success(`Fundo "${nome}" arquivado.`);
      onVoltar();
    } catch (e: any) {
      toast.error(`Falha ao arquivar: ${e?.message ?? 'erro'}`);
    } finally {
      setRemovendo(false);
    }
  };

  const baixar = () => {
    if (!salvo) return;
    try {
      exportarFundoXlsx(salvo);
    } catch (e: any) {
      toast.error(`Falha ao baixar: ${e?.message ?? 'erro'}`);
    }
  };

  const baixarDoc = async (anexo: DocumentoAnexo, versao?: DocumentoVersao) => {
    if (!anexo.documento_id) {
      toast.error('Documento ainda não registrado — não há o que baixar.');
      return;
    }
    try {
      await baixarDocumento(anexo.documento_id, versao?.numero_versao);
    } catch (e: any) {
      toast.error(`Falha ao baixar: ${e?.message ?? 'erro'}`);
    }
  };

  const substituirDoc = (anexo: DocumentoAnexo) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.pdf,.doc,.docx';
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      const acao = anexo.documento_id ? 'Nova versão de' : 'Documento';
      try {
        toast.info?.(`Enviando "${file.name}"...`);
        await enviarDocumento(fundoId, anexo, file);
        toast.success(`${acao} "${anexo.titulo ?? anexo.tipo}" enviado com sucesso.`);
        await carregar(); // recarrega o estado real (versões/atual do backend)
      } catch (e: any) {
        const det = e?.details ? ` — ${JSON.stringify(e.details)}` : '';
        toast.error(`Falha ao enviar documento: ${e?.message ?? 'erro'}${det}`);
      }
    };
    input.click();
  };

  if (carregando || (!salvo && !erro)) {
    return <SkeletonDetalhe onVoltar={onVoltar} />;
  }

  if (erro || !salvo) {
    return (
      <div className="fd-screen">
        <div className="fd-head">
          <BotaoVoltar onClick={onVoltar} />
        </div>
        <div className="fd-body">
          <div className="fl-empty">{erro ?? 'Fundo não encontrado.'}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="fd-screen">
      <div className="fd-head">
        <div className="fd-head-row">
          <div className="fd-head-main">
            <div className="fd-title-row">
              <BotaoVoltar onClick={onVoltar} />
              <h1 className="fd-title">{salvo.nome_fantasia || salvo.razao_social}</h1>
              {editando && <span className="fd-editing-tag">Editando</span>}
            </div>
            <div className="fd-meta">
              <span>{maskCnpj(salvo.cnpj)}</span>
            </div>
          </div>

          <div className="fd-actions">
            {editando ? (
              <>
                <button className="fd-btn fd-btn--ghost" onClick={cancelar} type="button" disabled={salvando}>
                  <X size={15} /> Cancelar
                </button>
                <button className="fd-btn fd-btn--primary" onClick={salvar} type="button" disabled={salvando}>
                  <Check size={15} /> {salvando ? 'Salvando...' : 'Salvar'}
                </button>
              </>
            ) : (
              <>
                <button
                  className="fd-icon-btn"
                  onClick={baixar}
                  type="button"
                  title="Baixar dados do fundo (Excel)"
                  aria-label="Baixar dados do fundo"
                >
                  <Download size={18} />
                </button>
                <div className="fd-menu-wrap" ref={menuRef}>
                  <button
                    className="fd-icon-btn"
                    onClick={() => setMenuAberto(o => !o)}
                    type="button"
                    title="Mais ações"
                    aria-haspopup="menu"
                    aria-expanded={menuAberto}
                    disabled={removendo}
                  >
                    <MoreVertical size={18} />
                  </button>
                  {menuAberto && (
                    <ul className="fd-menu" role="menu">
                      <li>
                        <button type="button" role="menuitem" onClick={() => { setMenuAberto(false); entrar(); }}>
                          <Pencil size={15} /> Editar
                        </button>
                      </li>
                      <li>
                        <button type="button" role="menuitem" className="fd-menu-danger" onClick={arquivar} disabled={removendo}>
                          <Archive size={15} /> {removendo ? 'Arquivando...' : 'Arquivar'}
                        </button>
                      </li>
                    </ul>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="fd-body fl-resumo">
        {editando && form ? (
          <>
            <FundoEditDados form={form} onChange={patch} />
            <ColecoesEdit form={form} onChange={patch} prestadorOpts={prestadorOpts} />
            <ResumoDocumentos form={form} onDownloadDoc={baixarDoc} onSubstituir={substituirDoc} />
          </>
        ) : (
          <ResumoSecoes form={salvo} onDownloadDoc={baixarDoc} onSubstituir={substituirDoc} />
        )}
      </div>
    </div>
  );
};

export default FundoDetalhe;
